'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { issueHumanPass } from '@/actions';
import { logger } from '@/utils';
import {
  HUMAN_PASS_RENEW_AFTER_MS,
  HUMAN_PASS_WAIT_TIMEOUT_MS,
} from '@/utils/humanPassPolicy';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type HumanPassStatus = 'idle' | 'loading' | 'ready' | 'error';

export type UseHumanPassGateReturn = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  status: HumanPassStatus;
  /** Resolve um novo desafio. Usar quando a cota do passe se esgota. */
  refresh: () => void;
  /**
   * Espera o passe existir antes de consultar.
   *
   * Resolve `true` quando há passe (ou quando não há portão configurado, caso
   * das WL sem Turnstile). Resolve `false` quando desistiu -- desafio recusado
   * ou teto de espera esgotado -- e aí a consulta deve seguir e tratar a
   * negativa como já trata hoje. Nunca rejeita.
   */
  waitForPass: (timeoutMs?: number) => Promise<boolean>;
  isConfigured: boolean;
  /**
   * O Cloudflare decidiu pedir interação e desenhou o desafio.
   *
   * Em `interaction-only` o widget fica com tamanho zero enquanto resolve
   * sozinho — que é o caso comum. Ganhar altura é o único sinal de que existe
   * algo para o usuário clicar, e é o que autoriza tirá-lo do canto e trazê-lo
   * para o centro da tela.
   */
  needsInteraction: boolean;
};

let scriptPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if (typeof window === 'undefined' || window.turnstile) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile script'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Resolve um desafio Turnstile e troca o token por um passe de humanidade.
 *
 * O desafio roda na abertura do formulário, não no submit: a consulta de CPF
 * dispara enquanto o usuário digita, então o passe precisa já existir nesse
 * momento.
 */
export const useHumanPassGate = (): UseHumanPassGateReturn => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [status, setStatusState] = useState<HumanPassStatus>('idle');
  const [needsInteraction, setNeedsInteraction] = useState(false);

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // O status precisa existir também fora do ciclo de render. O state do React
  // só chega para quem espera no render seguinte, e isso quebra a espera no
  // caso que mais importa: `refresh()` marca 'loading' e a consulta que vem
  // logo atrás ainda leria 'ready' do passe velho -- já esgotado -- e nasceria
  // negada de novo.
  const statusRef = useRef<HumanPassStatus>('idle');
  const waitersRef = useRef<Array<(ready: boolean) => void>>([]);

  const applyStatus = useCallback((next: HumanPassStatus) => {
    statusRef.current = next;
    setStatusState(next);

    // 'ready' e 'error' são os dois desfechos: um libera a consulta, o outro
    // manda seguir sem passe. Os intermediários não acordam ninguém.
    if (next !== 'ready' && next !== 'error') {
      return;
    }

    const waiters = waitersRef.current;
    waitersRef.current = [];
    waiters.forEach((resolve) => resolve(next === 'ready'));
  }, []);

  const waitForPass = useCallback(
    (timeoutMs: number = HUMAN_PASS_WAIT_TIMEOUT_MS): Promise<boolean> => {
      // Sem sitekey não existe portão: as WL sem Turnstile seguem consultando
      // na hora, como antes.
      if (!siteKey) {
        return Promise.resolve(true);
      }

      const current = statusRef.current;

      if (current === 'ready') {
        return Promise.resolve(true);
      }

      // 'error' não melhora com espera: o siteverify recusou o token e ninguém
      // vai emitir passe sem um desafio novo. Devolve false na hora para a
      // consulta seguir para a negativa, que já tem tratamento.
      if (current === 'error') {
        return Promise.resolve(false);
      }

      return new Promise<boolean>((resolve) => {
        // Num objeto porque `settle` precisa enxergar o timer que só é criado
        // depois dele -- e o timer precisa chamar `settle`.
        const pending: { settled: boolean; timer?: ReturnType<typeof setTimeout> } = {
          settled: false,
        };

        const settle = (ready: boolean) => {
          if (pending.settled) {
            return;
          }

          pending.settled = true;
          clearTimeout(pending.timer);
          waitersRef.current = waitersRef.current.filter((waiter) => waiter !== settle);
          resolve(ready);
        };

        waitersRef.current.push(settle);
        pending.timer = setTimeout(() => settle(false), timeoutMs);
      });
    },
    [siteKey],
  );

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    let cancelled = false;
    applyStatus('loading');

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'cpf-lookup',
          // Fixo em vez do 'auto' padrão, que seguiria o idioma do navegador:
          // o site inteiro é pt-BR, e um desafio em inglês no meio do checkout
          // parece coisa de terceiro e derruba confiança.
          language: 'pt-br',
          // Só aparece se o Cloudflare julgar que precisa de interação.
          appearance: 'interaction-only',
          callback: (token: string) => {
            void issueHumanPass(token)
              .then((result) => {
                if (!cancelled) {
                  applyStatus(result.ok ? 'ready' : 'error');
                }
              })
              .catch((error) => {
                logger.error('Falha ao emitir passe de humanidade:', error);
                if (!cancelled) {
                  applyStatus('error');
                }
              });
          },
          'error-callback': () => {
            if (!cancelled) {
              applyStatus('error');
            }
          },
          'expired-callback': () => {
            if (widgetIdRef.current) {
              window.turnstile?.reset(widgetIdRef.current);
            }
          },
        });
      })
      .catch((error) => {
        logger.error('Falha ao carregar o Turnstile:', error);
        if (!cancelled) {
          applyStatus('error');
        }
      });

    return () => {
      cancelled = true;

      if (widgetIdRef.current) {
        window.turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, applyStatus]);

  // Observa o tamanho do container: o Turnstile não avisa que passou a exigir
  // interação, mas desenhar o desafio muda a altura de zero para algo.
  useEffect(() => {
    const container = containerRef.current;

    if (!siteKey || !container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const { height, width } = entry.contentRect;
      setNeedsInteraction(height > 0 && width > 0);
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [siteKey]);

  const refresh = useCallback(() => {
    if (!widgetIdRef.current) {
      return;
    }

    applyStatus('loading');

    // O container só volta a ser exibido no render que este applyStatus dispara.
    // Resetar aqui desenharia um desafio interativo dentro de um elemento
    // ainda escondido, e o usuário não teria como resolvê-lo.
    requestAnimationFrame(() => {
      if (widgetIdRef.current) {
        window.turnstile?.reset(widgetIdRef.current);
      }
    });
  }, [applyStatus]);

  // Renova o passe antes de ele vencer, em vez de esperar a consulta ser negada.
  //
  // Ver HUMAN_PASS_RENEW_AFTER_MS: o passe vale 10 minutos e, até aqui, nada o
  // renovava -- quem ficava com a aba aberta durante o sorteio era negado na
  // consulta seguinte. O relógio só começa a contar quando existe passe, e é
  // reiniciado a cada passe novo, porque `status` volta a 'ready'.
  useEffect(() => {
    if (!siteKey || status !== 'ready') {
      return;
    }

    const timer = setTimeout(refresh, HUMAN_PASS_RENEW_AFTER_MS);

    return () => clearTimeout(timer);
  }, [siteKey, status, refresh]);

  return {
    containerRef,
    status,
    refresh,
    waitForPass,
    isConfigured: Boolean(siteKey),
    needsInteraction,
  };
};
